import i18n from 'services/i18n';
import { EN_US } from 'consts';

export const toFixedNumber = (num, digits, base) => {
  // eslint-disable-next-line no-restricted-properties
  const pow = Math.pow(base || 10, digits);
  return Math.round(num * pow) / pow;
};

export const formatNumberByLocale = (
  amount,
  currency,
  numberOfDigits,
  currencyDisplay = 'code',
  addMinusSign = false
) => {
  if (!currency) return;

  let total;
  try {
    total = Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency,
      currencyDisplay,
      maximumFractionDigits: numberOfDigits
    }).format(Math.abs(amount));

    if (i18n.language === 'is-IS' && currency === 'ISK') {
      total = total.replace(/[,.]/g, letter => (letter === '.' ? ',' : '.'));
    }

    if (addMinusSign) {
      total = total.replace(/(\d+(?:[.,]\d+)?)/, '-$1');
    }
  } catch (e) {
    console.error(e);
  }

  return total;
};

export const formatNumberToCurrencyParts = ({ amount, currency, locale }) => {
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      maximumFractionDigits: 2
    });
    return formatter.formatToParts(amount) || [];
  } catch (e) {
    console.error(e);
  }
};

export const getDynamicCurrencySeparators = ({ currency, locale }) => {
  // amount is hardcoded to 1000, in order to get both decimal and group separators (amount should be larger than 999)
  const parts = formatNumberToCurrencyParts({ amount: 1000, currency, locale });
  if (!parts) {
    return {};
  }
  const symbolIndex = parts.findIndex(part => part.type === 'currency');
  const symbol = symbolIndex > -1 ? parts[symbolIndex] : null;
  const group = parts.find(part => part.type === 'group');
  const decimal = parts.find(part => part.type === 'decimal');
  return {
    symbol: symbol
      ? {
          value: symbol?.value,
          isPrefix: symbolIndex === 0,
          isSuffix: symbolIndex === parts.length - 1
        }
      : null,
    groupSeparator: group?.value,
    decimalSeparator: decimal?.value
  };
};

export const formatNumberToStringByLocale = ({ number, locale }) => {
  if (!number) return '';

  let formattedValue;
  try {
    formattedValue = number?.toLocaleString(locale);
  } catch (err) {
    formattedValue = number?.toLocaleString(EN_US);
  }

  return formattedValue || '';
};

export const isUnixToday = unix => {
  const unixMilliseconds = unix * 1000;
  const today = new Date().setHours(0, 0, 0, 0);
  const otherDay = new Date(unixMilliseconds).setHours(0, 0, 0, 0);
  return today === otherDay;
};

export const timeSince = date => {
  const seconds = Math.floor((new Date() - date) / 1000);
  let interval = seconds / 31536000;
  if (interval > 1) {
    return {
      interval: Math.floor(interval),
      period: 'years'
    };
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return {
      interval: Math.floor(interval),
      period: 'months'
    };
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return {
      interval: Math.floor(interval),
      period: 'days'
    };
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return {
      interval: Math.floor(interval),
      period: 'hours'
    };
  }
  interval = seconds / 60;
  if (interval > 1) {
    return {
      interval: Math.floor(interval),
      period: 'minutes'
    };
  }
  return {
    interval: Math.floor(interval),
    period: 'seconds'
  };
};
